import { useState } from "react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { X, Plus, Trash2, GripVertical } from "lucide-react";
import { toast } from "sonner";
import SearchableSelect from "@/components/SearchableSelect";

export const STATUS_STYLES = {
  copied: "bg-[#EFF6FF] text-[#1E3A8A] border-l-4 border-[#93C5FD]",
  confirmed: "bg-[#F0FDF4] text-[#14532D] border-l-4 border-[#86EFAC]",
  present: "bg-[#FEF2F2] text-[#7F1D1D] border-l-4 border-[#FCA5A5]",
};

const enc = encodeURIComponent;

async function runMutation(fn, onChanged, errMsg) {
  try { await fn(); onChanged(); } catch (err) { toast.error(err.response?.data?.detail || errMsg); }
}

async function dropPersonOnSite(e, { site, monthKey, dayNum, onChanged }) {
  let payload = null;
  try { payload = JSON.parse(e.dataTransfer.getData("application/x-person")); } catch { return; }
  if (!payload?.person || payload.fromSite === site.name) return;
  if (site.assignments.some((a) => a.person === payload.person)) return;
  try {
    await api.post(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/assignments`, { person: payload.person });
    if (payload.fromSite) {
      await api.delete(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(payload.fromSite)}/assignments/${enc(payload.person)}`);
    }
    onChanged();
  } catch (err) {
    toast.error(err.response?.data?.detail || "Errore spostamento nominativo");
  }
}

function SiteCardHeader({ site, count, onDelete }) {
  return (
    <div className="bg-slate-100 px-3 py-2 border-b border-slate-300 flex justify-between items-center">
      <div data-testid={`site-drag-handle-${site.name}`} draggable
        onDragStart={(e) => { e.dataTransfer.setData("application/x-site", site.name); e.dataTransfer.effectAllowed = "move"; }}
        title="Trascina per riordinare i cantieri"
        className="flex items-center gap-1.5 cursor-grab active:cursor-grabbing select-none min-w-0">
        <GripVertical className="w-3.5 h-3.5 text-slate-400 shrink-0" />
        <h3 className="text-sm font-bold tracking-tight text-slate-800 uppercase break-words leading-tight">{site.name}</h3>
      </div>
      <div className="flex items-center gap-2 shrink-0">
        <span data-testid={`site-count-${site.name}`}
          className="text-xs font-mono font-bold text-slate-800 border border-slate-400 rounded-sm px-1.5 py-0.5 bg-white">{count}</span>
        <button data-testid={`site-delete-${site.name}`} className="print-hide text-slate-400 hover:text-red-700 transition-colors" onClick={onDelete}>
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      </div>
    </div>
  );
}

function cardBorderClass(dragOver) {
  return dragOver ? "border-blue-500 ring-2 ring-blue-200" : "border-slate-300";
}

function AddPersonBar({ candidates, onAddMany, siteName }) {
  const [pending, setPending] = useState([]);
  const available = candidates.filter((c) => !pending.includes(c));
  const commit = () => { onAddMany(pending); setPending([]); };
  return (
    <div className="print-hide px-3 py-2 border-t border-slate-200 space-y-1.5">
      {pending.length > 0 && (
        <div className="flex flex-wrap gap-1 items-center">
          {pending.map((p) => (
            <span key={p} data-testid={`add-person-pending-${siteName}-${p}`}
              className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-blue-50 border border-blue-200 text-blue-900 text-xs rounded-sm">
              {p}
              <button data-testid={`add-person-pending-remove-${siteName}-${p}`}
                onClick={() => setPending(pending.filter((x) => x !== p))}
                className="text-blue-400 hover:text-red-700 transition-colors">
                <X className="w-3 h-3" />
              </button>
            </span>
          ))}
          <Button data-testid={`add-person-commit-${siteName}`} size="sm"
            className="h-6 px-2 rounded-sm bg-slate-900 hover:bg-slate-700 text-white text-xs"
            onClick={commit}>
            Assegna {pending.length}
          </Button>
        </div>
      )}
      <SearchableSelect options={available} onSelect={(p) => setPending((cur) => [...cur, p])}
        placeholder="+ Aggiungi nominativi (selezione multipla)..." testId={`add-person-${siteName}`} />
    </div>
  );
}

function SiteAssignments({ site, bis, onCycle, onRemove, filter }) {
  if (!site.assignments.length) {
    return <p className="text-xs text-slate-400 px-3 py-2 italic">Nessun nominativo</p>;
  }
  // ordine alfabetico (italiano, case-insensitive) indipendente dall'ordine di inserimento
  const sorted = [...site.assignments].sort((a, b) => a.person.localeCompare(b.person, "it", { sensitivity: "base" }));
  return sorted.map((a) => (
    <AssignmentRow key={a.person} siteName={site.name} assignment={a}
      isBis={bis.includes(a.person)} filter={filter}
      onCycle={() => onCycle(a.person)} onRemove={() => onRemove(a.person)} />
  ));
}

function matchClassFor(person, filter) {
  const q = filter.trim().toLowerCase();
  if (!q) return "";
  if (person.toLowerCase().includes(q)) return " ring-2 ring-inset ring-amber-400 font-bold";
  return " opacity-40";
}

function AssignmentRow({ siteName, assignment: a, isBis, onCycle, onRemove, filter = "" }) {
  const matchClass = matchClassFor(a.person, filter);
  return (
    <div className="group flex items-center border-b last:border-0 border-slate-100"
      draggable
      onDragStart={(e) => e.dataTransfer.setData("application/x-person", JSON.stringify({ person: a.person, fromSite: siteName }))}
      title="Trascina su un altro cantiere per spostare il nominativo">
      <button
        data-testid={`personnel-item-${siteName}-${a.person}`}
        onClick={onCycle}
        title="Click: Blu → Verde → Rosso → Blu"
        className={`flex-1 text-left flex items-center justify-between px-3 py-1.5 text-sm font-medium tracking-tight cursor-pointer select-none transition-colors hover:brightness-95 ${STATUS_STYLES[a.status]}${matchClass}`}
      >
        <span>{a.person}</span>
        {isBis && (
          <span data-testid={`bis-badge-${a.person}`}
            className="ml-2 inline-flex items-center px-1.5 py-0.5 text-[10px] font-bold uppercase rounded-sm border bg-[#FFF7ED] text-[#C2410C] border-[#FED7AA]">
            BIS
          </span>
        )}
      </button>
      <button data-testid={`remove-${siteName}-${a.person}`}
        className="print-hide px-2 text-slate-300 hover:text-red-700 transition-colors"
        onClick={onRemove}>
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

function SubSection({ site, monthKey, dayNum, onChanged, subOptions }) {
  const [subName, setSubName] = useState("");
  const [subN, setSubN] = useState("");
  const [showForm, setShowForm] = useState(false);

  const addSub = () => runMutation(async () => {
    await api.post(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/subcontractors`,
      { name: subName, workforce: Number(subN) || 0 });
    setSubName(""); setSubN(""); setShowForm(false);
  }, onChanged, "Errore aggiunta subappaltatore");

  const removeSub = (name) => runMutation(
    () => api.delete(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/subcontractors/${enc(name)}`),
    onChanged, "Errore rimozione subappaltatore");

  return (
    <div className="bg-slate-50 px-3 py-2 border-t border-slate-200 text-xs text-slate-600">
      {site.subcontractors.map((s) => (
        <div key={s.name} data-testid={`sub-${site.name}-${s.name}`} className="flex items-center justify-between py-0.5">
          <span>{s.name} — <b>{s.workforce}</b> uomini</span>
          <button data-testid={`sub-delete-${site.name}-${s.name}`}
            className="print-hide text-slate-400 hover:text-red-700 transition-colors"
            onClick={() => removeSub(s.name)}>
            <X className="w-3 h-3" />
          </button>
        </div>
      ))}
      {showForm ? (
        <div className="print-hide flex gap-1 mt-1">
          <SearchableSelect options={subOptions || []} allowFree keepValue
            onSelect={setSubName} onType={setSubName}
            placeholder="Azienda" testId={`sub-name-${site.name}`} className="flex-1 min-w-0" />
          <Input data-testid={`sub-workforce-input-${site.name}`} value={subN} onChange={(e) => setSubN(e.target.value)}
            placeholder="N." type="number" min={0} className="h-6 text-xs rounded-sm w-16" />
          <Button data-testid={`sub-add-btn-${site.name}`} size="sm" className="h-6 px-2 rounded-sm bg-slate-900 hover:bg-slate-700 text-white"
            onClick={addSub} disabled={!subName.trim()}>
            <Plus className="w-3 h-3" />
          </Button>
        </div>
      ) : (
        <button data-testid={`sub-open-form-${site.name}`}
          className="print-hide mt-1 text-slate-500 hover:text-slate-900 font-semibold transition-colors"
          onClick={() => setShowForm(true)}>
          + Subappaltatore
        </button>
      )}
    </div>
  );
}

function buildSiteCardActions({ site, monthKey, dayNum, onChanged, setDragOver, onSiteMove }) {
  const run = (fn, errMsg) => runMutation(fn, onChanged, errMsg);

  const cycle = (person) => run(
    () => api.patch(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/assignments/${enc(person)}/cycle`),
    "Errore cambio stato"
  );

  const remove = (person) => run(
    () => api.delete(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/assignments/${enc(person)}`),
    "Errore rimozione"
  );

  const addMany = async (persons) => {
    try {
      for (const person of persons) {
        await api.post(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}/assignments`, { person });
      }
      if (persons.length > 1) toast.success(`${persons.length} nominativi assegnati a ${site.name}`);
      onChanged();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Errore aggiunta nominativi");
      onChanged();
    }
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    setDragOver(false);
    const draggedSite = e.dataTransfer.getData("application/x-site");
    if (draggedSite) {
      if (onSiteMove) onSiteMove(draggedSite, site.name);
      return;
    }
    await dropPersonOnSite(e, { site, monthKey, dayNum, onChanged });
  };

  const removeSite = () => {
    if (window.confirm(`Rimuovere il cantiere "${site.name}" dal giorno?`))
      run(() => api.delete(`/months/${enc(monthKey)}/days/${dayNum}/sites/${enc(site.name)}`), "Errore rimozione cantiere");
  };

  return { cycle, remove, addMany, removeSite, handleDrop };
}

export default function SiteCard({ site, stats, monthKey, dayNum, onChanged, baseList, subOptions, onSiteMove, filter = "" }) {
  const [dragOver, setDragOver] = useState(false);
  const bis = stats?.bis || [];
  const count = stats?.site_counts?.[site.name] ?? 0;
  const { cycle, remove, addMany, removeSite, handleDrop } =
    buildSiteCardActions({ site, monthKey, dayNum, onChanged, setDragOver, onSiteMove });

  const candidates = baseList.filter((p) => !site.assignments.some((a) => a.person === p));

  return (
    <div data-testid={`site-card-${site.name}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      className={`site-card flex flex-col bg-white border rounded-sm shadow-sm ${cardBorderClass(dragOver)}`}>
      <SiteCardHeader site={site} count={count} onDelete={removeSite} />
      <div className="flex-1">
        <SiteAssignments site={site} bis={bis} onCycle={cycle} onRemove={remove} filter={filter} />
      </div>
      <AddPersonBar candidates={candidates} onAddMany={addMany} siteName={site.name} />
      <SubSection site={site} monthKey={monthKey} dayNum={dayNum} onChanged={onChanged} subOptions={subOptions} />
    </div>
  );
}
